import { EXERCISE_LOAD_LIMITS, TRAINING_LIMITS, formatMinutes, setsTotalMinutes } from '@ukinfo/shared';
import { useId, useState } from 'react';
import { Field, Input, cn } from '../ui';

/**
 * Bereich "Empfohlene Dauer" im Uebungsformular: "von" und "bis" (bis ist optional) und aufklappbar
 * "Durchgaenge" (Anzahl, Minuten je Durchgang, Pause) mit Live-Anzeige der Gesamtdauer.
 *
 * Die Felder bleiben Text, damit sie beim Tippen leer sein duerfen. Sind Durchgaenge gesetzt und "von" und
 * "bis" leer, schlaegt das Formular die Gesamtdauer als "von" vor: "von" folgt den Durchgaengen, bis jemand es
 * selbst aendert.
 */

export interface LoadDraft {
  durationMinutes: string;
  durationMaxMinutes: string;
  sets: string;
  setMinutes: string;
  pauseMinutes: string;
}

/** Gesamtdauer der Durchgaenge im Entwurf; `null`, solange Anzahl oder Minuten fehlen oder unlesbar sind. */
export function draftSetsTotal(value: Pick<LoadDraft, 'sets' | 'setMinutes' | 'pauseMinutes'>): number | null {
  const sets = value.sets.trim() === '' ? NaN : Number(value.sets);
  const perSet = value.setMinutes.trim() === '' ? NaN : Number(value.setMinutes);
  const pause = value.pauseMinutes.trim() === '' ? 0 : Number(value.pauseMinutes);
  if (!Number.isFinite(sets) || !Number.isFinite(perSet) || !Number.isFinite(pause) || sets < 1 || perSet <= 0 || pause < 0) return null;
  return setsTotalMinutes(sets, perSet, pause);
}

const FIELDS = ['sets', 'setMinutes', 'pauseMinutes'] as const;

export function ExerciseLoadFields({
  value,
  onChange,
  errors,
  withSets,
}: {
  value: LoadDraft;
  onChange: (patch: Partial<LoadDraft>) => void;
  errors: Record<string, string>;
  /** Linkuebungen haben keine Durchgaenge. */
  withSets: boolean;
}): JSX.Element {
  const panelId = useId();
  const hasSets = FIELDS.some((field) => value[field].trim() !== '');
  const [setsOpen, setSetsOpen] = useState(hasSets);
  // "von" stammt aus den Durchgaengen (Vorschlag), solange es niemand selbst eingibt.
  const [suggested, setSuggested] = useState(false);
  const total = draftSetsTotal(value);
  const setsError = FIELDS.some((field) => errors[field]);

  const change = (patch: Partial<LoadDraft>): void => {
    if ('durationMinutes' in patch) {
      setSuggested(false);
      onChange(patch);
      return;
    }
    const touchesSets = FIELDS.some((field) => field in patch);
    if (!touchesSets) {
      onChange(patch);
      return;
    }
    const next = { ...value, ...patch };
    const nextTotal = draftSetsTotal(next);
    const free = value.durationMinutes.trim() === '' && value.durationMaxMinutes.trim() === '';
    if ((suggested || free) && nextTotal !== null) {
      setSuggested(true);
      // Die Einheit rechnet in ganzen Minuten: 8,5 wird 9.
      onChange({ ...patch, durationMinutes: String(Math.min(TRAINING_LIMITS.minutes, Math.max(1, Math.round(nextTotal)))) });
    } else if (suggested && nextTotal === null) {
      setSuggested(false);
      onChange({ ...patch, durationMinutes: '' });
    } else {
      onChange(patch);
    }
  };

  const open = setsOpen || setsError;

  return (
    <section aria-label="Empfohlene Dauer" className="space-y-3 rounded-xl border border-slate-200 p-3">
      <div>
        <h4 className="text-sm font-semibold text-slate-900">Empfohlene Dauer</h4>
        <p className="text-xs text-slate-500">Wie lange die Übung im Training dauern soll. „Bis“ ist freiwillig und ergibt eine Spanne, z. B. 10 bis 15 Minuten.</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Dauer von (Min.)" error={errors['durationMinutes']} hint={suggested ? 'Vorschlag aus den Durchgängen' : undefined}>
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="numeric"
              min={1}
              max={TRAINING_LIMITS.minutes}
              value={value.durationMinutes}
              onChange={(event) => change({ durationMinutes: event.target.value })}
            />
          )}
        </Field>
        <Field label="Dauer bis (Min.)" error={errors['durationMaxMinutes']} hint="Optional">
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="numeric"
              min={1}
              max={TRAINING_LIMITS.minutes}
              value={value.durationMaxMinutes}
              onChange={(event) => change({ durationMaxMinutes: event.target.value })}
            />
          )}
        </Field>
      </div>

      {withSets && (
        <div>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setSetsOpen(!open)}
            className="flex min-h-11 w-full items-center gap-2 text-left text-sm font-medium text-brand-700 sm:min-h-0"
          >
            <span aria-hidden="true" className={cn('inline-block transition-transform', open && 'rotate-90')}>▸</span>
            Durchgänge
            {!open && total !== null && <span className="font-normal text-slate-500">= {formatMinutes(total)} Min.</span>}
            {!open && total === null && <span className="font-normal text-slate-500">– freiwillig, z. B. 3 × 4 Min. mit Pause</span>}
          </button>
          <div id={panelId} hidden={!open} className="mt-2 space-y-2">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Anzahl" error={errors['sets']}>
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    inputMode="numeric"
                    min={EXERCISE_LOAD_LIMITS.sets.min}
                    max={EXERCISE_LOAD_LIMITS.sets.max}
                    step={1}
                    value={value.sets}
                    onChange={(event) => change({ sets: event.target.value })}
                  />
                )}
              </Field>
              <Field label="Minuten je Durchgang" error={errors['setMinutes']}>
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    inputMode="decimal"
                    min={EXERCISE_LOAD_LIMITS.setMinutes.min}
                    max={EXERCISE_LOAD_LIMITS.setMinutes.max}
                    step={EXERCISE_LOAD_LIMITS.step}
                    value={value.setMinutes}
                    onChange={(event) => change({ setMinutes: event.target.value })}
                  />
                )}
              </Field>
              <Field label="Pause (Min.)" error={errors['pauseMinutes']}>
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    inputMode="decimal"
                    min={EXERCISE_LOAD_LIMITS.pauseMinutes.min}
                    max={EXERCISE_LOAD_LIMITS.pauseMinutes.max}
                    step={EXERCISE_LOAD_LIMITS.step}
                    value={value.pauseMinutes}
                    onChange={(event) => change({ pauseMinutes: event.target.value })}
                  />
                )}
              </Field>
            </div>
            <p data-testid="sets-total" aria-live="polite" className="text-sm font-medium text-slate-800">
              {total !== null ? `= ${formatMinutes(total)} Min.` : 'Anzahl und Minuten je Durchgang eintragen – die Pause steht zwischen den Durchgängen.'}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
